import { Middleware } from '@reduxjs/toolkit';
import {
  createApi,
  fetchBaseQuery,
  BaseQueryFn,
  FetchBaseQueryError,
  FetchArgs,
} from '@reduxjs/toolkit/query/react';

import { RootState } from '@/redux/store';
import { camelizeKeys } from '@/utils/camelizeKeys';

/**
 * Set VITE_ENABLE_PROXY=true in your .env.development.local file if you would like to proxy
 * requests through your machine. This is useful for getting around CORS errors when trying to
 * hit Coral PROD locally during development.
 */
const baseUrl =
  import.meta.env.VITE_ENABLE_PROXY === 'true' ? '/v1/' : `${import.meta.env.VITE_API_URL}/v1/`;

let previousSalesChannel: string | null = null;

export const salesChannelMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action);

  const state = store.getState();
  const currentSalesChannel = state.myVisit.salesChannel;

  if (currentSalesChannel && currentSalesChannel !== previousSalesChannel) {
    previousSalesChannel = currentSalesChannel;
  }

  return result;
};

const baseQuery = fetchBaseQuery({
  baseUrl,
  // Give up on a hung request after 30s so it surfaces as an error.
  timeout: 30000,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.accessToken;
    const salesChannel = (getState() as RootState).myVisit.salesChannel;

    if (import.meta.env.VITE_CF_ACCESS_CLIENT_ID) {
      headers.set('CF-Access-Client-Id', import.meta.env.VITE_CF_ACCESS_CLIENT_ID);
    }

    if (import.meta.env.VITE_CF_ACCESS_CLIENT_SECRET) {
      headers.set('CF-Access-Client-Secret', import.meta.env.VITE_CF_ACCESS_CLIENT_SECRET);
    }

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    if (salesChannel) {
      headers.set('salesChannel', salesChannel);
    }

    return headers;
  },
});

export /**
 * Intercept Coral responses and convert keys to camelCase.
 *
 * @param {*} args
 * @param {*} api
 * @param {*} [extraOptions={}]
 * @return {*}
 */
const baseQueryWithCamelize: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions = {}
) => {
  const result = await baseQuery(args, api, extraOptions);
  if (result.data) {
    result.data = camelizeKeys(result.data as any);
  }
  return result;
};

// Define our single API slice object
export const api = createApi({
  baseQuery: baseQueryWithCamelize,
  endpoints: () => ({}),
  tagTypes: ['Cart', 'Passes', 'Rewards'],
});
